/**
 * SearchDialog — the one search, opened from the head of the sidebar or
 * the tab bar (or ⌘K / Ctrl+K anywhere), drawn as intake's is
 * (DESIGN-SYSTEM.md, Admin global navigation): before anything is typed it
 * is the next events, then the places and the settings sections in one
 * row of shortcuts; typed into, it is the events and the settings
 * sections that match. The search box on Events still narrows the list in
 * front of you, which is a different thing.
 *
 * Events are asked of the server, as the Events list asks (`search`);
 * settings are the section index beside the settings page. Each group is
 * at most five, with the way to the whole list where there may be more.
 * The first row is picked from the start, so Enter goes somewhere.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { listEvents, type Booking } from "../../lib/events-api";
import { formatDateRange } from "../../lib/format-utils";
import {
  SETTINGS_SECTIONS,
  findSections,
  settingsPath,
  type SettingsSectionId,
} from "../../lib/settings-index";
import { useDismissable } from "../../lib/use-dismissable";
import { useModalScrollLock } from "../../lib/use-modal-scroll-lock";
import { DESTINATIONS } from "./destinations";
import {
  BuildingIcon,
  ClipboardListIcon,
  CreditCardIcon,
  FileTextIcon,
  PlugIcon,
  TruckIcon,
  UserIcon,
} from "./icons";
import styles from "./SearchDialog.module.css";

const SETTLE_MS = 200;
const EACH = 5;

/** The settings sections, each with its mark. */
const SECTION_ICONS: Record<SettingsSectionId, ReactNode> = {
  profile: <UserIcon />,
  general: <BuildingIcon />,
  templates: <FileTextIcon />,
  payments: <CreditCardIcon />,
  operations: <TruckIcon />,
  integrations: <PlugIcon />,
};

type Hit = { key: string; to: string; title: string; line?: string; icon: ReactNode };
type Group = { label: string; hits: Hit[]; more?: string; reading?: boolean; compact?: boolean };

function eventHit(b: Booking): Hit {
  const title = b.event_name || b.client_name || b.quote_number;
  const when = b.event_date ? formatDateRange(b.event_date, b.event_end_date, "short") : "";
  return {
    key: `event:${b.id}`,
    to: `/events/${b.id}`,
    title,
    line: [b.quote_number, when].filter(Boolean).join(" · "),
    icon: <ClipboardListIcon />,
  };
}

function sectionHit(id: SettingsSectionId, label: string): Hit {
  return {
    key: `section:${id}`,
    to: settingsPath(id),
    title: label,
    icon: SECTION_ICONS[id],
  };
}

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  useModalScrollLock(open);
  useDismissable({ open, onDismiss: onClose, pointer: "none" });
  if (!open) return null;
  // On <body>, so nothing the page stacks above the sidebar shows through.
  return createPortal(<Search onClose={onClose} />, document.body);
}

function Search({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [events, setEvents] = useState<Booking[] | null>(null);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const q = query.trim();

  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    setEvents(null);
    const ctl = new AbortController();
    const settle = setTimeout(
      () => {
        listEvents(
          { search: q, sort: "upcoming", order: "desc", limit: EACH, offset: 0 },
          ctl.signal,
        )
          .then((page) => setEvents(page.bookings))
          .catch(() => {
            if (!ctl.signal.aborted) setEvents([]);
          });
      },
      q ? SETTLE_MS : 0,
    );
    return () => {
      clearTimeout(settle);
      ctl.abort();
    };
  }, [q]);

  const groups = useMemo<Group[]>(() => {
    const eventGroup = (label: string): Group => ({
      label,
      hits: (events ?? []).map(eventHit),
      more:
        q && events && events.length >= EACH
          ? `/events?${new URLSearchParams({ q })}`
          : "",
      reading: events === null,
    });
    if (!q) {
      const shortcuts: Hit[] = [
        ...DESTINATIONS.map<Hit>((d) => ({
          key: `place:${d.to}`,
          to: d.to,
          title: d.label,
          icon: d.icon,
        })),
        ...SETTINGS_SECTIONS.map(({ id, label }) => sectionHit(id, label)),
      ];
      return [
        eventGroup("Upcoming events"),
        { label: "Go to", hits: shortcuts, compact: true },
      ].filter((g) => g.reading || g.hits.length > 0);
    }
    const settings = findSections(q)
      .slice(0, EACH)
      .map(({ id, label }) => sectionHit(id, label));
    return [eventGroup("Events"), { label: "Settings", hits: settings }].filter(
      (g) => g.reading || g.hits.length > 0,
    );
  }, [q, events]);

  const flat = groups.flatMap((g) => g.hits);
  const current = Math.min(active, flat.length - 1);

  // The picked row is kept in view as the arrow keys move it.
  useEffect(() => {
    list.current
      ?.querySelector<HTMLElement>("[data-active='true']")
      ?.scrollIntoView?.({ block: "nearest" });
  }, [current]);

  const go = (to: string) => {
    onClose();
    void navigate(to);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive(Math.min(flat.length - 1, current + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive(Math.max(0, current - 1));
    } else if (e.key === "Enter") {
      const hit = flat[current];
      if (hit) go(hit.to);
    }
  };

  let n = -1;
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
      >
        {/* A search field, the one input DESIGN-SYSTEM.md lets carry a placeholder. */}
        <input
          ref={input}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKey}
          placeholder="Search events or settings"
          aria-label="Search everything"
          className={styles.input}
        />
        <div ref={list} className={styles.results}>
          {groups.length === 0 && (
            <p className={styles.none}>Nothing matches that.</p>
          )}
          {groups.map((g) => (
            <section key={g.label} aria-label={g.label}>
              <h2 className={styles.groupLabel}>{g.label}</h2>
              {g.reading && g.hits.length === 0 ? (
                <p className={styles.none}>Searching…</p>
              ) : (
                <ul className={g.compact ? styles.chips : styles.hits}>
                  {g.hits.map((h) => {
                    n += 1;
                    const i = n;
                    return (
                      <li key={h.key}>
                        <button
                          type="button"
                          data-active={i === current}
                          onClick={() => go(h.to)}
                          onMouseMove={() => i !== current && setActive(i)}
                          className={
                            g.compact
                              ? i === current
                                ? styles.chipActive
                                : styles.chip
                              : i === current
                                ? styles.hitActive
                                : styles.hit
                          }
                        >
                          <span className={styles.icon} aria-hidden="true">
                            {h.icon}
                          </span>
                          <span className={styles.hitTitle}>{h.title}</span>
                          {h.line && (
                            <span className={styles.hitLine}>{h.line}</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {g.more && (
                <button
                  type="button"
                  className={styles.more}
                  onClick={() => go(g.more!)}
                >
                  All events matching “{q}”
                </button>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
